import {
  GroupedBalancesByAccount,
  GroupedBalancesByWalletType,
  HubUserCredentials,
  WalletType,
  CreateStreakRequest,
  CreateStreakRequestV2,
  JoinStreakQueueRequestV2,
  type MinimumRequiredBalanceResponse,
} from "@cede/types";
import {
  API_ENDPOINT,
  ApiHelper,
  isServerApiError,
  Logger,
  PUBLIC_API_ENDPOINT,
  ENDPOINT_SCHEMAS,
  APP_SOURCE,
} from "@cede/utils";
import { clientConfig } from "@/lib/config/client-config";
import { ExchangeAccountFormData } from "@/store/accountStore";

// todo finish env var refactoring and use useApi hook (move the logic to the hooks)
// Instantiate ApiHelper using the validated, imported config with automatic schema parsing
export const apiHelper = new ApiHelper({ ...clientConfig, appSource: APP_SOURCE.HUB }, ENDPOINT_SCHEMAS);

// One connection carries both leaderboard rankings and PnL/token/price events
// (the PnL snapshot stream was merged into the leaderboard stream).
export const getLeaderboardSseEndpoint = (campaignName: string, epochId: string) =>
  `${clientConfig.apiUrl}/v1/hub/leaderboard/sse?campaign=${campaignName}&epoch=${epochId}`;

export const getNotificationsSseEndpoint = (token: string | null, provider?: string, identityToken?: string | null) => {
  // `app` identifies the frontend so the server can gate app-specific feeds
  // (global stats are Hub-only). Always sent — the stream also runs in public
  // mode (no token) for logged-out users. Query params carry auth because
  // EventSource can't set headers.
  const params = new URLSearchParams({ app: APP_SOURCE.HUB });
  if (token) {
    params.set("token", token);
    if (provider) params.set("provider", provider);
    if (identityToken) params.set("identityToken", identityToken);
  }
  return `${clientConfig.apiUrl}/v1/hub/notifications/sse?${params.toString()}`;
};

export const getOrderBookSseEndpoint = (pairSymbol: string, exchangeId: string) =>
  `${clientConfig.kenobiApiUrl}/${PUBLIC_API_ENDPOINT.ORDERBOOK_SUBSCRIBE}?pairSymbol=${pairSymbol}&exchangeId=${exchangeId}`;

export const getExchangeAccountBalances = async (
  auth: HubUserCredentials,
  accountId: string,
  walletTypes: string[],
  forceRefresh?: boolean,
) => {
  const queryParams: Record<string, string> = {
    accountId,
    forceRefresh: forceRefresh ? "true" : "false",
  };
  if (walletTypes.length > 0) {
    queryParams.walletTypes = walletTypes.join(",");
  }
  const balancesByAccountByWalletType = await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_EXCHANGE_ACCOUNT_BALANCES, {
    queryParams,
  });
  if (isServerApiError(balancesByAccountByWalletType)) {
    Logger.error("getExchangeAccountBalances failed:", balancesByAccountByWalletType);
    return {};
  }

  const groupedBalances: GroupedBalancesByAccount = {};
  Object.keys(balancesByAccountByWalletType).forEach((accountId) => {
    const balancesByWalletType = balancesByAccountByWalletType[accountId] || [];
    const groupedBalancesByWalletType = Object.entries(balancesByWalletType).reduce(
      (acc, [walletType, balancesByToken]) => {
        Object.entries(balancesByToken).forEach(([tokenSymbol, balance]) => {
          const existingBalanceForToken = acc[tokenSymbol];
          if (!existingBalanceForToken) {
            acc[tokenSymbol] = {
              totalBalance: balance.totalBalance,
              freeBalance: balance.freeBalance,
              usedBalance: balance.usedBalance,
              refTotalBalance: balance.refTotalBalance,
              refFreeBalance: balance.refFreeBalance,
              refUsedBalance: balance.refUsedBalance,
            };
          } else {
            existingBalanceForToken.totalBalance += balance.totalBalance;
            existingBalanceForToken.refTotalBalance += balance.refTotalBalance;
            existingBalanceForToken.freeBalance += balance.freeBalance;
            existingBalanceForToken.refFreeBalance += balance.refFreeBalance;
            existingBalanceForToken.usedBalance += balance.usedBalance;
            existingBalanceForToken.refUsedBalance += balance.refUsedBalance;
          }
          if (acc[tokenSymbol]) {
            acc[tokenSymbol][walletType as WalletType] = {
              totalBalance: balance.totalBalance,
              freeBalance: balance.freeBalance,
              usedBalance: balance.usedBalance,
              refTotalBalance: balance.refTotalBalance,
              refFreeBalance: balance.refFreeBalance,
              refUsedBalance: balance.refUsedBalance,
            };
          }
        });
        return acc;
      },
      {} as GroupedBalancesByWalletType,
    );
    groupedBalances[accountId] = groupedBalancesByWalletType;
  });
  return groupedBalances;
};

export const postExchangeAccount = async (auth: HubUserCredentials, form: ExchangeAccountFormData) => {
  // Use imported apiHelper
  return apiHelper.postAuth(auth, API_ENDPOINT.HUB_CREATE_EXCHANGE_ACCOUNT, {
    bodyParams: {
      exchangeId: form.selectedCex,
      publicApiKey: form.apiKey,
      privateApiKey: form.secretKey,
      password: form.password,
      uid: form.uid,
    },
  });
};

export const deleteExchangeAccount = async (auth: HubUserCredentials, accountId: string) => {
  // Use imported apiHelper
  return apiHelper.deleteAuth(auth, API_ENDPOINT.HUB_CREATE_EXCHANGE_ACCOUNT, {
    pathParams: [accountId],
  });
};

export const getUserPhaseVolume = async (
  campaignName: string,
  epochId: number,
  phaseId: number,
  auth: HubUserCredentials,
) => {
  // Use imported apiHelper
  const result = await apiHelper.getWithAuth(auth, API_ENDPOINT.USER_PHASE_VOLUME, {
    queryParams: {
      campaign: campaignName,
      epochId: String(epochId),
      phaseId: String(phaseId),
    },
  });
  if (isServerApiError(result)) {
    Logger.error("getUserPhaseVolume failed:", result);
    return null;
  }
  return result.userVolume;
};

export const getUserVolumeInWindow = async (campaignName: string, auth: HubUserCredentials) => {
  const { currentWindowVolume, nextWindowDate } = await apiHelper.getWithAuth(
    auth,
    API_ENDPOINT.HUB_USER_VOLUME_IN_WINDOW,
    {
      queryParams: { campaign: campaignName },
    },
  );
  return { currentWindowVolume, nextWindowDate };
};

export const updateAutomaticStreakCguAcknowledgment = async (
  auth: HubUserCredentials,
  params: { signature: string; nonce: number },
) => {
  return apiHelper.postAuth(auth, API_ENDPOINT.HUB_USER_AUTOMATIC_STREAK_CGU_ACKNOWLEDGMENT, {
    bodyParams: params,
  });
};

// Automatic Streak API functions
export const createAutomaticStreak = async (auth: HubUserCredentials, request: CreateStreakRequest) => {
  return apiHelper.postAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_CREATE, {
    bodyParams: request,
  });
};

export const createAutomaticStreakV2 = async (auth: HubUserCredentials, request: CreateStreakRequestV2) => {
  return apiHelper.postAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_CREATE_V2, {
    bodyParams: request,
  });
};

export const joinAutomaticStreakQueueV2 = async (auth: HubUserCredentials, request: JoinStreakQueueRequestV2) => {
  return apiHelper.postAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_JOIN_QUEUE_V2, {
    bodyParams: request,
  });
};

export const leaveAutomaticStreakQueueV2 = async (auth: HubUserCredentials, campaignName: string) => {
  return apiHelper.deleteAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_LEAVE_QUEUE_V2, {
    pathParams: [campaignName],
  });
};

export const getUserAutomaticStreakQueueItemV2 = async (auth: HubUserCredentials, campaignName: string) => {
  return apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_GET_QUEUE_ITEM_V2, {
    pathParams: [campaignName],
  });
};

export const increaseAutomaticStreakQueuePriorityV2 = async (
  auth: HubUserCredentials,
  campaignName: string,
  ticketsToSpend: number,
) => {
  return apiHelper.postAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_QUEUE_PRIORITY_TOPUP_V2, {
    pathParams: [campaignName, "priority"],
    bodyParams: { ticketsToSpend },
  });
};

export const getAutopilotPriorityTicketBalance = async (auth: HubUserCredentials) => {
  return apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_PRIORITY_TICKETS);
};

export const getAutomaticStreakStatus = async (auth: HubUserCredentials, streakId: string) => {
  return apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_GET, {
    pathParams: [streakId],
  });
};

export const getAutomaticStreakStatusV2 = async (auth: HubUserCredentials, streakId: string) => {
  return apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_GET_V2, {
    pathParams: [streakId],
  });
};

export const cancelAutomaticStreak = async (auth: HubUserCredentials, streakId: string) => {
  return apiHelper.deleteAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_CANCEL, {
    pathParams: [streakId],
  });
};

export const cancelAutomaticStreakV2 = async (auth: HubUserCredentials, streakId: string) => {
  return apiHelper.deleteAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_CANCEL_V2, {
    pathParams: [streakId],
  });
};

export const getUserAutomaticStreaks = async (auth: HubUserCredentials, campaignName?: string) => {
  return apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_LIST, {
    queryParams: campaignName ? { campaignName } : undefined,
  });
};

export const getUserAutomaticStreaksV2 = async (auth: HubUserCredentials, campaignName?: string) => {
  return apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_LIST_V2, {
    queryParams: campaignName ? { campaignName } : undefined,
  });
};

export const getAutomaticStreakConfig = async (auth: HubUserCredentials) => {
  return apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_CONFIG);
};

export const getAutomaticStreakCguStatus = async (auth: HubUserCredentials) => {
  return apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_CGU_STATUS);
};

export const getAutomaticStreakEpochLimit = async (auth: HubUserCredentials, campaignName: string, epochId: number) => {
  return apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_EPOCH_LIMIT, {
    pathParams: [campaignName, String(epochId)],
  });
};

export const getRecentlyCancelledStreaks = async (auth: HubUserCredentials) => {
  return apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_RECENTLY_CANCELLED);
};

export const getMinimumRequiredBalance = async (
  auth: HubUserCredentials,
  params: {
    campaignName: string;
    epochId: number;
    exchangeAccountId: number;
    totalTargetVolume: string;
  },
): Promise<MinimumRequiredBalanceResponse> => {
  return apiHelper.postAuth(auth, API_ENDPOINT.HUB_AUTOMATIC_STREAK_MINIMUM_REQUIRED_BALANCE, {
    bodyParams: params,
  });
};
